import { useEffect, useMemo, useState } from "react";
import { Form, Input, Modal, Rate, Select, Space, Spin, Table, TableColumnsType, TableProps } from "antd";
import '@xyflow/react/dist/style.css';
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { analysisCreateRequest, analysisListRequest, favoriteModelsRequest, selectedModelsRequest } from "src/store/actions/operations";
import { StoreState } from "src/store/configureStore";
import { AsyncStates } from "src/constants";
import { LoadingOutlined } from "@ant-design/icons";

interface DataType {
  key: React.Key;
  name: string;
  age: number;
  address: string;
}

interface Values {
  title?: string;
  description?: string;
  modifier?: string;
}

const columns: TableColumnsType<DataType> = [
  {
    title: 'Name',
    dataIndex: 'name',
    render: (text: string) => text,
  },
  {
    title: 'Status',
    dataIndex: 'status',
  },
  {
    title: 'Created By',
    dataIndex: 'createdBy',
  },
  {
    title: 'Created On',
    dataIndex: 'createdOn',
  },
];

// rowSelection object indicates the need for row selection
const rowSelection: TableProps<DataType>['rowSelection'] = {
  onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
    console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
  },
  getCheckboxProps: (record: DataType) => ({
    disabled: record.name === 'Disabled User', // Column configuration not to be checked
    name: record.name,
  }),
};

export const Analysis = ({ open, setOpen }: { open: boolean, setOpen: (open: boolean) => void }) => {
  const [selectionType] = useState<'checkbox' | 'radio'>('checkbox');
  const [form] = Form.useForm();
  const [, setFormValues] = useState<Values>();

  const dispatch = useDispatch()

  const onCreate = (values: Values) => {
    console.log('Received values of form: ', values);
    dispatch(analysisCreateRequest({ opid, ...values }))
    setFormValues(values);
    setOpen(false);
  };

  // const onChange = (key: string) => {
  //   console.log(key);
  // };

  const { push } = useHistory()

  const { opid } = useParams<{ opid: string }>()

  useEffect(() => {
    dispatch(analysisListRequest(opid))
  }, [dispatch, opid])

  const analysisList = useSelector((state: StoreState) => state.operations.analysisListData)
  const analysisListStatus = useSelector((state: StoreState) => state.operations.analysisListStatus)
  const analysisData = useMemo(() => analysisList?.map((o: any) => ({ key: o.id, ...o })), [analysisList])
  const getSavedModels = (expanded: boolean, record: any) => {
    expanded && dispatch(selectedModelsRequest(record.key));
  }
  return (
    <Spin spinning={analysisListStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Table<DataType>
          rowSelection={{ type: selectionType, ...rowSelection }}
          columns={columns}
          dataSource={analysisData}
          onRow={(record, rowIndex) => ({ onClick: () => { push(`/ai-engine/operations/${opid}/analysis/${record.key}/categorize`) } })}
          expandable={{ expandedRowRender: record => <SavedModelsTable />, onExpand: getSavedModels }}
        />
      </Space>
      <Modal
        title="Create New Analysis"
        open={open}
        okText="Create"
        cancelText="Cancel"
        okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
        onCancel={() => setOpen(false)}
        destroyOnClose
        modalRender={dom => (
          <Form
            layout="vertical"
            form={form}
            name="form_in_modal"
            initialValues={{ modifier: 'public' }}
            onFinish={(values) => onCreate(values)}
          >
            {dom}
          </Form>
        )}
      >
        <>
          <br />
          <Form.Item
            name="name"
            label="Analysis Name"
            rules={[{ required: true, message: 'Please input the title of operation!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="source" label="Source Analysis">
            <Select
              options={analysisList.map((ana: any) => ({ value: ana.id, label: ana.name }))}
              placeholder="Source analysis (optional)"
              allowClear
            />
          </Form.Item>
          <br />
        </>
      </Modal>
    </Spin>
  );
}

export const SavedModelsTable = () => {
  const dispatch = useDispatch()
  const { anid } = useParams<{ anid: string }>()
  const savedModels = useSelector((state: StoreState) => state.operations.selectedModelsData)
  const savedModelsStatus = useSelector((state: StoreState) => state.operations.selectedModelsStatus)
  const savedModelsData = useMemo(() => savedModels?.map((o: any) => ({ key: o.model_id, ...o })), [savedModels])
  const setFavorite = (value: number, record: any) => {
    dispatch(favoriteModelsRequest({ model_ids: [record.key], anid, favorite: value === 1 }));
  }
  return (
    <Spin spinning={savedModelsStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
      <Table
        title={() => <Space><span>Selected Models</span></Space>}
        style={{ marginBlock: 16 }}
        columns={[
          { title: '', dataIndex: 'favorite', key: 'favorite', render: (text: boolean, record: any) => <Rate count={1} defaultValue={record.favorite ? 1 : 0} onChange={(value: number) => { setFavorite(value, record) }} /> },
          { title: 'Category Combo', dataIndex: 'category_combo', key: 'combo' },
          { title: 'Model Type', dataIndex: 'model_type', key: 'model_type' }
        ]}
        dataSource={savedModelsData}
        pagination={false}
      />
    </Spin>
  )
}